/**
 * Estado de revisión de un gasto. Los valores son los del contrato de Tracking.
 *
 * `ReturnedToDriver` se llamaba `Rejected` hasta que el rechazo dejó de ser una
 * sola cosa. Hoy hay dos decisiones negativas y sólo una admite vuelta atrás,
 * así que la palabra «rechazado» se reserva para la definitiva. El valor
 * numérico no se movió: nada de lo guardado ni de lo ya enviado cambia.
 */
export enum ExpenseStatus {
  /** Reportado por el conductor, esperando revisión. */
  Pending = 0,
  /** Validado por operaciones. */
  Approved = 1,
  /** El conductor debe corregirlo; al reenviarlo desde la app vuelve a Pendiente. */
  ReturnedToDriver = 2,
  /**
   * Rechazo definitivo: ese dinero no se abona y el conductor no puede hacer
   * nada. La app no tiene vuelta atrás desde aquí porque el reenvío sólo acepta
   * `ReturnedToDriver`.
   */
  Rejected = 3,
}

/**
 * Orden de presentación en los desplegables de filtro, formulario y tabla.
 *
 * **No es el orden numérico**, y no debe "corregirse" para que lo sea: los dos
 * estados en los que el gasto sigue vivo van primero, y los dos que lo cierran
 * después. Un gasto devuelto está esperando al conductor, así que se parece
 * mucho más a uno pendiente que a uno rechazado, aunque su valor sea el 2.
 */
export const EXPENSE_STATUSES: readonly ExpenseStatus[] = [
  ExpenseStatus.Pending,
  ExpenseStatus.ReturnedToDriver,
  ExpenseStatus.Approved,
  ExpenseStatus.Rejected,
] as const;

/**
 * Decisiones que operaciones puede tomar sobre un gasto.
 *
 * No hay transiciones prohibidas: cualquier estado puede ir a cualquier otro,
 * `Pending` incluido.
 */
export type ExpenseDecision = ExpenseStatus;

/**
 * Estados que exigen motivo. El backend responde 400 con `ReasonRequired` si
 * falta, y el conductor lo verá en la app, así que se valida también en el front.
 */
/**
 * Las dos decisiones negativas se explican de forma distinta, así que piden
 * cosas distintas. Antes había un único `requiresReason` para las dos, y dejó de
 * describir nada en cuanto la devolución pasó a tener catálogo propio.
 *
 * - Devolver pide **código** del catálogo; su texto sólo amplía.
 * - Rechazar no tiene catálogo, así que sigue pidiendo **texto**.
 */
export function takesReturnReason(status: ExpenseStatus): boolean {
  return status === ExpenseStatus.ReturnedToDriver;
}

export function requiresFreeText(status: ExpenseStatus): boolean {
  return status === ExpenseStatus.Rejected;
}

/** Estados que conservan el texto libre; el resto lo descartan. */
export function keepsFreeText(status: ExpenseStatus): boolean {
  return (
    status === ExpenseStatus.ReturnedToDriver ||
    status === ExpenseStatus.Rejected
  );
}

export function isExpenseStatus(value: unknown): value is ExpenseStatus {
  return EXPENSE_STATUSES.includes(value as ExpenseStatus);
}
